import { inject, Injectable, SimpleChange, SimpleChanges } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';

import { ElementsState, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementBindings,
  CanvasElementId,
  CanvasElementInput,
  CanvasElementProperties,
  getFormGroupLikeWrapper,
  WithProps,
} from '@blaze-runtime/engine-domain';
import {
  CanvasElementInputBinding,
  CanvasElementPropertiesWithData,
  DataBindingSourceInfo,
  FormContext,
  getActiveBinding,
  RenderElementContext,
} from '@blaze-shared/elements-domain';
import { countedShareReplay, isDeepEqual } from '@blaze-shared/shared-util';

import { getFormIdForField } from '../../../+state/elements/elements.selectors';
import { FormStateService } from '../../../services/forms/runtime/public-api';
import {
  CanvasElementInputData,
  RenderElementService,
} from '../../../services/render-element.service';
import { SchemaValidationService } from '../../../services/schema-validation.service';
import { findPrimaryContainerForNavigationElement } from '../../../utils/canvas-element/path';
import { isSelfBindingForm } from '../../../utils/elements/form.utils';
import {
  isDataFilter,
  isFormGroupLike,
  isFormList,
  isNavigationElement,
  isVariantOfFormContainer,
} from '../../element-model/element-type-guards';
import { getWrapperRefs } from '../../element-model/wrapper-refs';
import { ElementReadinessState, RenderElementInstance } from '../../models/render-element.models';
import { BindInputsConfig, DataInputsReadyMap } from '../io-data.models';

import { ElementInputProviderService } from './element-input-data-provider.service';

interface GetInputDataStreamConfig {
  bindingDef: CanvasElementInput;
  element: CanvasElement & WithProps<CanvasElementProperties>;
  isDataContainerValueSetUpdater: () => void;
  elementInstance: RenderElementInstance | undefined;
  elementContext$: Observable<RenderElementContext | undefined>;
  elementReadinessState$: Observable<ElementReadinessState>;
}

@Injectable({ providedIn: 'root' })
export class ElementInputsBinding {
  private readonly elementInputProviderService = inject(ElementInputProviderService);
  private readonly formStateService = inject(FormStateService);
  private readonly renderElementService = inject(RenderElementService);
  private readonly store = inject(Store<ElementsState>);
  private readonly schemaValidationService = inject(SchemaValidationService);

  bindInputs({
    element,
    elementInstance,
    elementInputs,
    elementContext$,
    dataInitialized$,
    componentInitialized$,
    setComponentInput,
    isDataContainerValueSetUpdater,
    isHiddenByBindingUpdater,
    dataInputsReadyUpdater,
    changesNotifier,
  }: BindInputsConfig): Observable<void> {
    const isFormVariantEl = isVariantOfFormContainer(element);
    const isFormGroupEl = isFormGroupLike(element);
    const isFormListEl = isFormList(element);
    const isDataFilterEl = isDataFilter(element);

    elementInstance.bindingResults = {} as CanvasElementBindings;
    const inputStreams$: Observable<CanvasElementInputData>[] = [];
    const elementReadinessState$ = combineLatest({
      componentInitialized: componentInitialized$,
      dataInitialized: dataInitialized$,
      isRuntimeInfoFetched: this.store.select(runtimeSelectors.selectIsRuntimeInfoFetched),
    }).pipe(
      filter(
        ({ componentInitialized, dataInitialized }) => componentInitialized && dataInitialized,
      ),
      countedShareReplay(),
    );

    const blockingInputs: CanvasElementInputBinding[] = [];
    /// inputs resolver
    for (const bindingDef of elementInputs) {
      let input$: Observable<CanvasElementInputData> | undefined;

      const inputDataStreamConfig: GetInputDataStreamConfig = {
        bindingDef,
        element,
        elementReadinessState$,
        elementInstance,
        elementContext$,
        isDataContainerValueSetUpdater,
      };
      /**
       * For navigation elements use inputs from its primary container to resolve visibility
       */
      if (isNavigationElement(element) && bindingDef.name === 'visible') {
        input$ = this.getInputDataStreamForNavigationElement(inputDataStreamConfig);
      } else {
        input$ = this.getInputDataStream(inputDataStreamConfig);
      }

      if (input$) {
        inputStreams$.push(input$);

        if (!bindingDef.isNonBlockingInput) {
          blockingInputs.push(bindingDef.name);
        }
      }
    }

    setComponentInput('bindingResults', elementInstance.bindingResults);
    const inputs$ = this.renderElementService.createInputStreamsHandler(
      inputStreams$,
      blockingInputs,
    );

    return inputs$.pipe(
      concatLatestFrom(() => [
        // Concat form context if the element is a field of a form
        this.getFormContextForElement(element.id),
        componentInitialized$,
      ]),
      map(([inputs, formContext, componentInitialized]) => {
        if (!elementInstance) {
          throw new Error(`no element instance`);
        }
        const changes: SimpleChanges = {};
        let changesCount = 0;
        for (const { valueResult, isPaginatedOnServer, inputName, dataAdapter } of inputs) {
          const previousValue = elementInstance[inputName];
          const { data, updateSource } = valueResult;
          const newValue = dataAdapter(data, formContext);
          const isFormLikeElement = (isFormVariantEl || isFormListEl) && inputName === 'value';

          // Validate the input data and schema
          // TODO: Uncomment when the element input schema is corrected and unified (BL-6414)
          // this.schemaValidationService.validateElementInputData(
          //   element,
          //   inputName,
          //   valueResult,
          //   newValue,
          // );

          // Update data input payload
          elementInstance.dataInput = {
            key: inputName,
            ...valueResult,
            data: newValue,
          };

          // Inform about value update of the data container
          if (isFormLikeElement || isDataFilterEl) {
            isDataContainerValueSetUpdater();
          }

          if (
            (isDeepEqual(previousValue, newValue) &&
              (element.type !== 'signature' || newValue !== null)) ||
            (updateSource === 'initial' && previousValue && newValue === null)
          ) {
            continue;
          }

          changesCount++;

          if (!isFormLikeElement) {
            elementInstance[inputName] = newValue;
            changes[inputName] = new SimpleChange(previousValue, newValue, !componentInitialized);

            if (elementInstance.paginationProps?.state && inputName === 'value') {
              elementInstance.paginationProps.isServerSide = !!isPaginatedOnServer;
            }
          }
        }

        // Process changes
        if (changesCount > 0) {
          changesNotifier(changes);

          // Inform about value update of the data container
          if (isFormGroupEl && changes['value']) {
            isDataContainerValueSetUpdater();
            const formIds = getFormGroupLikeWrapper(getWrapperRefs(element)).map(
              ({ formId }) => formId,
            );
            for (const formId of formIds) {
              this.formStateService.reset(formId);
            }
          }

          // Update visibility
          if (changes['visible']) {
            // Hide element if invisible and not in the editor
            isHiddenByBindingUpdater(
              !!elementInstance?.bindingResults?.['visible'] && !elementInstance?.visible,
            );
          }
        }

        // Update inputs which were initialized and received initial data
        const loadedKeys = inputs.reduce<DataInputsReadyMap>((acc, { inputName }) => {
          acc[inputName] = true;
          return acc;
        }, {});
        dataInputsReadyUpdater(loadedKeys);
      }),
    );
  }

  private getInputDataStream({
    bindingDef,
    element,
    elementReadinessState$,
    isDataContainerValueSetUpdater,
    elementInstance,
    elementContext$,
  }: GetInputDataStreamConfig): Observable<CanvasElementInputData> | undefined {
    const isFormVariant = isVariantOfFormContainer(element);
    const data = (element.props as CanvasElementPropertiesWithData)?.data;

    const binding =
      getActiveBinding(data, bindingDef.name) ?? data?.find(({ key }) => key === bindingDef.name);

    const isFormSelfBinding =
      isFormVariant &&
      bindingDef.name === 'value' && // handle value binding only
      isSelfBindingForm(element);

    if (
      binding?.enabled === false /*is explicitly disabled*/ ||
      (!isFormSelfBinding && !binding?.dataSource?.sourceType) ||
      (binding?.dataSource?.sourceType === 'expression' &&
        !binding.dataSource?.expression?.length) /* is expression but no expression is defined */
    ) {
      if (isFormVariant && bindingDef.name === 'value') {
        // unbound form
        isDataContainerValueSetUpdater();
      }
      return undefined;
    }

    let dataSource: DataBindingSourceInfo | undefined;
    let bindings: Record<string, string> | undefined;

    if (isFormSelfBinding) {
      dataSource = {
        id: element.id,
        name: 'formContainer',
        sourceType: 'self',
        columns: [],
      };
      bindings = {};
    } else if (!binding) {
      return undefined;
    } else if (binding?.dataSource) {
      dataSource = binding.dataSource;
      bindings = binding.bindings;
    }

    if (!dataSource || elementInstance?.bindingResults?.[bindingDef.name]) {
      return undefined;
    }

    if (!elementInstance) {
      throw new Error(`no element instance (${element.id})`);
    }

    elementInstance.bindingResults = {
      ...elementInstance.bindingResults,
      [bindingDef.name]: true,
    };

    const dataAdapter = this.elementInputProviderService.getDataAdapter(
      element,
      bindingDef.isDefaultDataSourceInput,
    );
    // NOTE sub shouldn't be triggered before element.props are set
    return elementReadinessState$.pipe(
      concatLatestFrom(() => elementContext$),
      concatMap(([{ isRuntimeInfoFetched }, elementContext]) => {
        if (!isRuntimeInfoFetched || !dataSource) {
          return EMPTY;
        }
        return combineLatest([
          this.elementInputProviderService.getInputDataStream({
            inputName: bindingDef.name,
            source: dataSource,
            bindings,
            element,
            binding,
            rowContext: elementContext?.rowContext,
          }),
          this.store
            .select(runtimeSelectors.selectWorkflowsPaginationById(dataSource.id))
            .pipe(distinctUntilChanged()),
        ]).pipe(
          map(([valueResult, isPaginatedOnServer]) => {
            return {
              valueResult,
              isPaginatedOnServer,
              inputName: bindingDef.name,
              dataAdapter,
              dataSource,
            };
          }),
        );
      }),
    );
  }

  private getInputDataStreamForNavigationElement(
    config: GetInputDataStreamConfig,
  ): Observable<CanvasElementInputData> {
    return this.renderElementService.entityState$.pipe(
      map((elementsState) =>
        findPrimaryContainerForNavigationElement(elementsState, config.element),
      ),
      distinctUntilChanged(),
      filter(Boolean),
      map((primaryContainer) =>
        this.getInputDataStream({
          ...config,
          element: primaryContainer,
        }),
      ),
      filter(Boolean),
      switchMap((inputDataStream$) => inputDataStream$),
    );
  }

  /** Fetch form context if the element is a field of a form */
  private getFormContextForElement(
    elementId: CanvasElementId,
  ): Observable<FormContext | undefined> {
    return this.store.select(getFormIdForField(elementId)).pipe(
      concatMap((formId) =>
        formId ? this.store.select(runtimeSelectors.selectFormContextById(formId)) : of(undefined),
      ),
      first(),
    );
  }
}
